import { useMemo, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { useSimilarSites } from "app/services/sitesService/hooks/useSimilarSites";
import { useAutoCompleteClick } from "pro-features/components/AutocompleteWebCategories/utils";

import { generateAutomationName } from "../../../../helpers/generateAutomationName";
import type { PrimaryWebsite } from "../../../SetupModal/types";
import type { AutocompleteWebsitesWithKeysControlSchema } from "../../../Shared/AutocompleteWebsitesWithKeysControl/AutocompleteWebsitesWithKeysControl";
import { AutocompleteWebsitesWithKeysControl } from "../../../Shared/AutocompleteWebsitesWithKeysControl/AutocompleteWebsitesWithKeysControl";
import { LOADER_HEIGHT, LOADER_WIDTH, SIMILAR_ITEM_TYPE } from "../constants";
import { AlertIcon, MessageInfo, MessageWarning } from "../styles";
import { LIMIT_EXCEEDED, LIMIT_REACHED_P1, LIMIT_REACHED_P2, PLACEHOLDER_TEXT } from "../texts";

export type DomainListAutocompleteProps = Omit<
    AutocompleteWebsitesWithKeysControlSchema,
    | "autocompleteRef"
    | "onItemClick"
    | "placeholder"
    | "preventTruncateUnlessForced"
    | "renderOnEmptyQuery"
    | "resetValueOnSelect"
    | "searchIcon"
> & {
    currentSize: number;
    dataAutomationNames: string[];
    isLoading?: boolean;
    limit: number;
    primaryDomain?: string;
    onItemClick: (item: PrimaryWebsite, clearTextField: () => void) => void;
};

type AutocompleteRefObject = {
    textFieldRef?: {
        clearValue: () => void;
    };
    truncateResults?: (flag: boolean) => void;
};

export const DomainListAutocomplete = ({
    currentSize,
    dataAutomationNames,
    isLoading,
    limit,
    primaryDomain,
    onItemClick,
    ...autocompleteProps
}: DomainListAutocompleteProps) => {
    const { translate } = useTranslate();
    const autocompleteRef = useRef<AutocompleteRefObject>(null);

    useAutoCompleteClick(autocompleteRef, true, "AutocompleteWebsitesBase");

    const hasEmptyDomainSlots = currentSize < limit;

    const { data: similarSitesData = [] } = useSimilarSites(
        primaryDomain ?? "",
        { limit: 30 },
        { enabled: !!primaryDomain && hasEmptyDomainSlots },
    );
    const similarSites = useMemo(
        () =>
            similarSitesData.map(({ Domain, Favicon }) => ({
                name: Domain,
                image: Favicon,
                type: SIMILAR_ITEM_TYPE,
            })),
        [similarSitesData],
    );

    const renderSimilarSites = () => similarSites.filter(({ name }) => !autocompleteProps.exclude?.includes(name));

    const clearTextField = () => {
        autocompleteRef.current?.textFieldRef?.clearValue();
    };

    const handleItemClick = (item: PrimaryWebsite) => {
        autocompleteRef.current?.truncateResults?.(true);

        onItemClick(item, clearTextField);
    };

    return (
        <Flex
            data-automation={generateAutomationName("settings", [
                ...dataAutomationNames,
                "autocomplete",
                "section",
                "wrapper",
            ])}
        >
            {hasEmptyDomainSlots ? (
                <Flex
                    data-automation={generateAutomationName("settings", [
                        ...dataAutomationNames,
                        "autocomplete",
                        "wrapper",
                    ])}
                    flexGrow={1}
                >
                    {isLoading ? (
                        <span
                            data-automation={generateAutomationName("settings", [
                                ...dataAutomationNames,
                                "autocomplete",
                                "loading",
                            ])}
                        >
                            <PixelPlaceholderLoader
                                height={LOADER_HEIGHT}
                                width={LOADER_WIDTH}
                            />
                        </span>
                    ) : (
                        <AutocompleteWebsitesWithKeysControl
                            {...autocompleteProps}
                            autocompleteRef={autocompleteRef}
                            onItemClick={handleItemClick}
                            placeholder={translate(PLACEHOLDER_TEXT)}
                            preventTruncateUnlessForced
                            renderOnEmptyQuery={renderSimilarSites}
                            resetValueOnSelect
                            searchIcon="add"
                        />
                    )}
                </Flex>
            ) : (
                <Stack gap="12px">
                    <MessageWarning isVisible={currentSize > limit}>
                        <AlertIcon
                            iconName="alert-circle"
                            size="sm"
                        />
                        {translate(LIMIT_EXCEEDED, { number: limit })}
                    </MessageWarning>
                    <MessageInfo isVisible>
                        <b>{translate(LIMIT_REACHED_P1)}</b> &nbsp;
                        {translate(LIMIT_REACHED_P2)}
                    </MessageInfo>
                </Stack>
            )}
        </Flex>
    );
};
